import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import UpdateProductsPopup from './UpdateProductsPopup';
import { Edit, Publish, SwapVert, UnpublishedTwoTone, DragIndicator } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import Events from '../service/Events';
import AdminAPI from '../service/AdminAPI';

const headCells = [
  {
    id: 'name',
    numeric: false,
    disablePadding: true,
    label: 'Name des Produkts',
  },
  {
    id: "published",
    numeric: false,
    disablePadding: true,
    label: "Status"
  },
  {
    id: 'price',
    numeric: true,
    disablePadding: false,
    label: 'Preis',
  },
  {
    id: 'weight',
    numeric: true,
    disablePadding: false,
    label: 'Gewicht',
  }
];

function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount } = props;

  return (
    <TableHead>
      <TableRow
        sx={{
          background: 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)',
        }}
      >
        <TableCell 
          padding="checkbox"
          sx={{
            color: 'white',
            fontWeight: 700,
            fontSize: { xs: '0.75rem', sm: '0.95rem' },
          }}
        >
          
        </TableCell>
        <TableCell padding="checkbox">
          <Checkbox
            sx={{
              color: 'white',
              '&.Mui-checked': {
                color: 'white',
              },
              '&.MuiCheckbox-indeterminate': {
                color: 'white',
              }
            }}
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all products',
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: { xs: '0.75rem', sm: '0.95rem' },
              py: { xs: 1, sm: 2 }
            }}
          >
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const EnhancedTableToolbar = (props) => {
  const { numSelected, onEdit, onDeleteProducts, onPublish, onUnpublish, onSwap } = props;

  return (
    <>
      <Toolbar
        sx={{
          pl: { xs: 1, sm: 2 },
          pr: { xs: 1, sm: 1 },
          borderRadius: '12px 12px 0 0',
          minHeight: { xs: '56px !important', sm: '64px !important' },
          ...(numSelected > 0 && {
            bgcolor: '#e8f5e9',
            borderBottom: '2px solid #2e7d32',
          }),
        }}
      >
        {numSelected > 0 ? (
          <Typography
            sx={{ 
              flex: '1 1 100%',
              color: '#2e7d32',
              fontWeight: 700,
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }}
            variant="subtitle1"
            component="div"
          >
            {numSelected} ausgewählt
          </Typography>
        ) : (
          <Typography
            sx={{ 
              flex: '1 1 100%',
              color: '#2e7d32',
              fontWeight: 700,
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
            variant="h6"
            id="tableTitle"
            component="div"
          >
            Produkte
          </Typography>
        )}

        {numSelected > 0 ? (
          <>
            {
              numSelected === 2 ? (
                <Tooltip onClick={onSwap} title='Tauschen der Sortierung von zwei selektierten Produkten' >
                  <IconButton>
                    <SwapVert />
                  </IconButton>
                </Tooltip>
              ) : (<></>)
            }
            <Tooltip title="Veröffentlichen von ausgewählten Produkten" onClick={() =>
              Events.onAlert.emit("warning", "Möchten Sie alle ausgewählten Produkte veröffentlichen?", "Somit werden diese Produkte wieder auf der Startseite angezeigt und können ausgewählt werden."
                , () => {
                  onPublish();
                })
            }>
              <IconButton sx={{ color: '#2e7d32', '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.1)' } }}>
                <Publish />
              </IconButton>
            </Tooltip>
            <Tooltip title="Verbergen von ausgewählten Produkten" onClick={() =>
              Events.onAlert.emit("warning", "Möchten sie alle ausgewählten Produkte verbergen?", "Somit werden diese Produkte nicht mehr auf der Produktseite angezeigt und in der Tabelle zum runterladen ignoriert (Alle Bestellungen mit diesen Produkten werden auch automatisch gelöscht)."
                , () => {
                  onUnpublish();
                })
            }>
              <IconButton sx={{ color: '#ed6c02', '&:hover': { bgcolor: 'rgba(237, 108, 2, 0.1)' } }}>
                <UnpublishedTwoTone />
              </IconButton>
            </Tooltip>
            <Tooltip title="Löschen" onClick={() =>
              Events.onAlert.emit("warning", "Möchten Sie alle ausgewählten Produkte löschen?", "Wenn Sie alle ausgewählten Produkte löschen, werden auch alle Bestellungen für dieses Produkt gelöscht"
                , () => {
                  onDeleteProducts();
                })
            }>
              <IconButton sx={{ color: '#d32f2f', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' } }}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Bearbeiten" onClick={() => onEdit()}>
              <IconButton sx={{ color: '#2e7d32', '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.1)' } }}>
                <Edit />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
          </>
        )}
      </Toolbar>
    </>
  );
};

export default class AllProductsTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      order: 'asc',
      orderBy: 'price',
      selected: [],
      page: 0,
      dense: false,
      rowsPerPage: 5,
      products: props.products,
      show: false,
      isReordering: false  // Flag to prevent state override during reordering
    };
  }

  componentDidUpdate(prevProps) {
    // Only update if props changed AND we're not in the middle of reordering
    if (this.props.products !== prevProps.products && !this.state.isReordering) {
      this.setState({ page: 0, products: this.props.products })
    }
  }

  handleRequestSort = (event, property) => {
    const isAsc = this.state.orderBy === property && this.state.order === 'asc';
    this.setState({ order: isAsc ? 'desc' : 'asc', orderBy: property });
  };

  handleSelectAllClick = (event) => {
    if (event.target.checked) {
      this.setState({ selected: this.state.products });
      return;
    }

    this.setState({ selected: [] });
  };

  handleClick = (event, id) => {
    const selectedIndex = this.state.products.findIndex(p => p.ID === id);

    if (selectedIndex === -1) {
      return;
    }

    const copiedArr = Array.from(this.state.selected);

    if (copiedArr.length <= 0) {
      copiedArr.push(this.state.products[selectedIndex]);

      this.setState({ selected: copiedArr });
      return;
    }

    const ind = copiedArr.findIndex(p => p.ID === id);

    if (ind !== -1) {
      copiedArr.splice(ind, 1)
    } else {
      copiedArr.push(this.state.products[selectedIndex]);
    }

    this.setState({ selected: copiedArr });
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: parseInt(event.target.value, 10), page: 0 });
  };

  handleChangeDense = (event) => {
    this.setState({ dense: event.target.checked });
  };

  isSelected = (id) => this.state.selected.findIndex(s => s.ID === id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  emptyRows =
    this.state ? this.state.page > 0 ? Math.max(0, (1 + this.state.page) * this.state.rowsPerPage - this.state.products.length) : 0 : 5;

  deleteProducts = () => {
    AdminAPI.DeleteProducts({ productIDs: this.state.selected.map(_prod => _prod.ID) })
      .then((_res) => {
        window.location.reload();
      })
      .catch(console.error);
  }

  swapProducts = () => {
    AdminAPI.SwapOrderOfProducts(this.state.selected.map(product => product.SortID))
      .then(() => window.location.reload())
      .catch(console.error);
  }

  publishAll = () => {
    AdminAPI.PublishAllSelected(this.state.selected.map(_prod => _prod.ID))
      .then((_res) => {
        window.location.reload();
      })
      .catch(console.error);
  }

  unpublishAll = () => {
    AdminAPI.UnpublishAllSelected(this.state.selected.map(_prod => _prod.ID))
      .then((_res) => {
        window.location.reload();
      })
      .catch(console.error);
  }

  onDragEnd = (result) => {
    // Disable drag if filtered
    if (this.props.isFiltered) {
      return;
    }

    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    // Reorder products array
    const reorderedProducts = Array.from(this.state.products);
    const [movedProduct] = reorderedProducts.splice(sourceIndex, 1);
    reorderedProducts.splice(destinationIndex, 0, movedProduct);

    // Update SortID for each product based on new position
    reorderedProducts.forEach((product, index) => {
      product.SortID = index + 1;
    });

    // Update local state immediately and set reordering flag
    this.setState({ 
      products: reorderedProducts,
      isReordering: true 
    });

    // Prepare the data to send to backend - assign new SortIDs based on new positions
    const productsToUpdate = reorderedProducts.map((product, index) => ({
      id: product.ID,
      sortId: index + 1  // New SortID based on position (1-indexed)
    }));

    // Send to backend
    AdminAPI.ReorderProducts(productsToUpdate)
      .then(() => {
        // Show success notification
        Events.onResult.emit('success', 'Produkte wurden erfolgreich neu sortiert!');
        
        // Update parent state
        if (this.props.onProductsReordered) {
          this.props.onProductsReordered(reorderedProducts);
        }
        
        // Clear reordering flag
        this.setState({ isReordering: false });
      })
      .catch((err) => {
        console.error(err);
        // Show error notification
        Events.onResult.emit('error', 'Fehler beim Sortieren der Produkte');
        // Revert on error
        this.setState({ 
          products: this.props.products,
          isReordering: false 
        });
      });
  }

  render() {
    return (
      <>
        <Box sx={{ width: '100%' }}>
          <Paper sx={{ 
            width: '100%', 
            mb: 2,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(46, 125, 50, 0.15)',
            border: '1px solid #e8f5e9'
          }}>
            <EnhancedTableToolbar
              onSwap={this.swapProducts}
              numSelected={this.state.selected.length}
              onEdit={() => this.setState({ show: true })}
              onDeleteProducts={this.deleteProducts}
              onPublish={this.publishAll}
              onUnpublish={this.unpublishAll}
            />
            <DragDropContext onDragEnd={this.onDragEnd}>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table
                  sx={{ minWidth: { xs: 300, sm: 750 } }}
                  aria-labelledby="tableTitle"
                  size={this.state.dense ? 'small' : 'medium'}
                >
                  <EnhancedTableHead
                    numSelected={this.state.selected.length}
                    order={this.state.order}
                    orderBy={this.state.orderBy}
                    onSelectAllClick={this.handleSelectAllClick}
                    onRequestSort={this.handleRequestSort}
                    rowCount={this.state.products.length}
                  />
                  <Droppable droppableId="products-table">
                    {(provided, snapshot) => (
                      <TableBody
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {this.state && this.state.products
                          .map((product, index) => {
                            const isItemSelected = this.isSelected(product.ID);
                            const labelId = `enhanced-table-checkbox-${index}`;

                            return (
                              <Draggable
                                key={product.ID}
                                draggableId={`product-${product.ID}`}
                                index={index}
                                isDragDisabled={this.props.isFiltered}
                              >
                                {(provided, snapshot) => (
                                  <TableRow
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...(!this.props.isFiltered ? provided.dragHandleProps : {})}
                                    hover
                                    role="checkbox"
                                    aria-checked={isItemSelected}
                                    tabIndex={-1}
                                    selected={isItemSelected}
                                    sx={{
                                      cursor: this.props.isFiltered ? 'default' : 'grab',
                                      bgcolor: snapshot.isDragging ? '#e8f5e9' : 'inherit',
                                      '&:hover': {
                                        bgcolor: '#f1f8f4 !important',
                                      },
                                      '&.Mui-selected': {
                                        bgcolor: '#e8f5e9 !important',
                                        '&:hover': {
                                          bgcolor: '#d4edda !important',
                                        },
                                      },
                                      '&:active': {
                                        cursor: this.props.isFiltered ? 'default' : 'grabbing'
                                      }
                                    }}
                                  >
                                    <TableCell padding="checkbox">
                                      <DragIndicator sx={{ 
                                        color: this.props.isFiltered ? '#ccc' : '#2e7d32', 
                                        cursor: this.props.isFiltered ? 'not-allowed' : 'grab',
                                        opacity: this.props.isFiltered ? 0.5 : 1
                                      }} />
                                    </TableCell>
                                    <TableCell 
                                      padding="checkbox"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        this.handleClick(event, product.ID);
                                      }}
                                      sx={{ cursor: 'pointer' }}
                                    >
                                      <Checkbox
                                        color="primary"
                                        checked={isItemSelected}
                                        inputProps={{
                                          'aria-labelledby': labelId,
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell
                                      component="th"
                                      id={labelId}
                                      scope="row"
                                      padding="none"
                                    >
                                      {product.Name}
                                    </TableCell>
                                    <TableCell 
                                      align='left'
                                    >
                                      {product.Published ? "Veröffentlicht" : "Nicht veröffentlicht"}
                                    </TableCell>
                                    <TableCell 
                                      align="right"
                                    >
                                      {product.isFixed ? product.Price + "€" : "Individuell"}
                                    </TableCell>
                                    <TableCell 
                                      align="right"
                                    >
                                      {product.isFixed ? product.Weight + " Kg\\L" : "Individuell"}
                                    </TableCell>
                                  </TableRow>
                                )}
                              </Draggable>
                            );
                          })}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </Table>
              </TableContainer>
            </DragDropContext>
            <TablePagination
              labelRowsPerPage="Zeilen pro Spalte:"
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={this.state.products.length}
              rowsPerPage={-1}
              page={this.state.page}
              onPageChange={this.handleChangePage}
              onRowsPerPageChange={this.handleChangeRowsPerPage}
            />
          </Paper>
        </Box>
        <UpdateProductsPopup maxProducts={this.props.maxProducts} show={this.state.show} products={this.state.selected} onClose={() => this.setState({ show: false })} />
      </>
    );
  }
}